import { useQuery } from '@tanstack/react-query'
import { IssueIcon, Label, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { trpc } from '../../../utils/trpc'
import type { EvaluationDetailItem } from '../components/EvaluationDetailModal'
import { EvaluationDetailModal } from '../components/EvaluationDetailModal'

const PAGE_SIZE = 100

interface CycleEvaluationsTabProps {
	/** For testing: inject data directly without tRPC */
	_testData?: EvaluationDetailItem[] | null
	/** For testing: inject loading state */
	_testLoading?: boolean
	/** For testing: inject the API meta.total value (defaults to data length) */
	_testTotal?: number
}

export function CycleEvaluationsTab({ _testData, _testLoading, _testTotal }: CycleEvaluationsTabProps = {}) {
	const { cycleId } = useParams<{ cycleId: string }>()
	const [searchParams, setSearchParams] = useSearchParams()
	const [selectedEvaluation, setSelectedEvaluation] = useState<EvaluationDetailItem | null>(null)

	// Defense-in-depth: the tab is also hidden from the nav when the flag is off,
	// but a deep link could still hit this route. Show a placeholder + skip the
	// tRPC query (which the router would also strip includeRemoved from anyway).
	const { isEnabled: softDeleteEnabled, isLoading: flagLoading } = useFeatureFlag('participant-soft-delete')

	const { data: queryData, isLoading: queryLoading } = useQuery({
		...trpc.admin.cycles.evaluations.search.queryOptions({
			cycleId: cycleId as string,
			includeRemoved: true,
			page: 1,
			perPage: PAGE_SIZE,
			sortBy: 'lastName',
			order: 'asc',
		}),
		enabled: !!cycleId && _testData === undefined && softDeleteEnabled,
	})

	const evaluations: EvaluationDetailItem[] = _testData ?? (queryData?.data as EvaluationDetailItem[]) ?? []

	// Deep-link entrypoint for "View Full Evaluation" from the HR Review Flags
	// queue: when the page is opened with ?evaluationId=<id>, auto-open the
	// detail modal for that evaluation as soon as the list resolves. The hook
	// must run unconditionally — early returns below would otherwise change
	// hook order across renders and break rules-of-hooks.
	useEffect(() => {
		const targetId = searchParams.get('evaluationId')
		if (!targetId || selectedEvaluation || evaluations.length === 0) return
		const match = evaluations.find((e) => e.evaluationId === targetId)
		if (match) setSelectedEvaluation(match)
	}, [searchParams, evaluations, selectedEvaluation])

	if (!flagLoading && !softDeleteEnabled && _testData === undefined) {
		return (
			<div className='mt-6'>
				<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl p-12 flex flex-col items-center gap-3'>
					<IssueIcon size={32} className='text-neutral-300' />
					<Text variant='small' className='text-neutral-500'>
						The evaluations view is not enabled for this organization.
					</Text>
				</div>
			</div>
		)
	}

	// Strip the query param when the modal closes so refresh / back-nav isn't
	// surprising. Keeps the URL clean once the user is interacting normally.
	const handleCloseModal = () => {
		setSelectedEvaluation(null)
		if (searchParams.has('evaluationId')) {
			searchParams.delete('evaluationId')
			setSearchParams(searchParams, { replace: true })
		}
	}
	const isLoading = _testLoading ?? (queryLoading && _testData === undefined)
	const total = _testTotal ?? queryData?.meta?.total ?? evaluations.length
	const isTruncated = total > evaluations.length

	if (isLoading) {
		return (
			<div className='mt-6' data-testid='evaluations-loading'>
				<SpinnerWithBackground />
			</div>
		)
	}

	return (
		<div className='mt-6'>
			{isTruncated && (
				<div
					data-testid='evaluations-truncated-banner'
					className='mb-4 flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4'>
					<IssueIcon size={20} className='text-yellow-600 mt-0.5 shrink-0' />
					<Text variant='small' className='text-yellow-900'>
						Showing the first {evaluations.length} of {total} evaluations. Pagination is not yet available for this
						view.
					</Text>
				</div>
			)}
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				{evaluations.length === 0 ? (
					<div className='p-12 flex flex-col items-center gap-3'>
						<IssueIcon size={32} className='text-neutral-300' />
						<Text variant='small' className='text-neutral-500'>
							No evaluations found for this cycle.
						</Text>
					</div>
				) : (
					<table className='w-full'>
						<thead>
							<tr className='border-b border-neutral-100'>
								<th className='text-left p-4'>
									<Text variant='small' weight='semibold' className='text-neutral-600'>
										Participant
									</Text>
								</th>
								<th className='text-left p-4'>
									<Text variant='small' weight='semibold' className='text-neutral-600'>
										Reviewers
									</Text>
								</th>
							</tr>
						</thead>
						<tbody>
							{evaluations.map((evaluation) => (
								<tr
									key={evaluation.evaluationId}
									data-testid={`eval-row-${evaluation.evaluationId}`}
									className='border-b border-neutral-50 hover:bg-neutral-50 cursor-pointer transition-colors'
									onClick={() => setSelectedEvaluation(evaluation)}>
									<td className='p-4'>
										<div
											className='flex items-center gap-2'
											data-testid={`eval-name-${evaluation.isRemoved ? 'removed' : 'active'}`}>
											{evaluation.isRemoved ? (
												<>
													<del className='text-neutral-400'>
														{evaluation.firstName} {evaluation.lastName}
													</del>
													<Label color='red'>Removed</Label>
												</>
											) : (
												<Text variant='small'>
													{evaluation.firstName} {evaluation.lastName}
												</Text>
											)}
										</div>
									</td>
									<td className='p-4'>
										<Text variant='small' className={evaluation.isRemoved ? 'text-neutral-400' : ''}>
											{evaluation.reviewerCount}
										</Text>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{selectedEvaluation !== null && (
				<EvaluationDetailModal isOpen onClose={handleCloseModal} evaluation={selectedEvaluation} />
			)}
		</div>
	)
}
